const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * Read a content field that may or may not still be wrapped.
 *
 * A shared component is handed content by callers that migrate one story at a
 * time: some already pass flat values, the rest still pass Sitecore's
 * `{ value: … }` shape. The flows library's `StepHeader` and `Error` have dozens
 * of callers each, and `AddressFinder` is rendered across the site, so none of
 * their props can be narrowed without changing every caller in one go. Reading
 * either shape is what lets those callers move independently.
 *
 * It lives here rather than in either library because both use it, and this is
 * the one library both may depend on.
 *
 * A wrapped field is an object carrying a `value` key. A flat one is a string,
 * or an object without one. That is the same test the Sitecore adapter makes,
 * and for the same reason it is not merely `'value' in field`: authored content
 * has `value` properties of its own, so an option like `{ name, value, label }`
 * must not be mistaken for a field and collapsed.
 *
 * Delete this once no caller passes the wrapped shape, and narrow the props.
 */
/**
 * Metadata a wrapped field may carry beside `value`.
 *
 * This must stay in step with `FIELD_KEYS` in `libs/cms-sitecore/src/mappers.ts`.
 * The two cannot share one definition, because this library deliberately has no
 * Sitecore dependency, so they are kept aligned by hand and both list the reason.
 * A narrower list here would leave a genuine field wrapped and render an object:
 * `metadata` appears in the Experience Editor and `linktype` on route-level path
 * fields.
 *
 * Editor markup takes three names, because JSS does not have one name for it: a
 * text field carries `editable`, a link carries `editableFirstPart` and
 * `editableLastPart`. Listing only `editable` left every link wrapped in the
 * Experience Editor.
 */
const FIELD_KEYS = new Set(['value', 'editable', 'editableFirstPart', 'editableLastPart', 'metadata', 'linktype']);

export const readField = <T>(field: unknown): T | undefined => {
  if (!isObject(field)) return field as T | undefined;
  const keys = Object.keys(field);
  const isWrapped = keys.includes('value') && keys.every(key => FIELD_KEYS.has(key));
  return isWrapped ? (field.value as T) : (field as T);
};
